import { NumberLineBoxQuestionMark } from "assets/icons/Common";
import { Row } from "components/primitives";
import React, { FC } from "react";
import Draggable from "react-draggable";
import { Resizable, ResizeCallbackData } from "react-resizable";
import styled from "styled-components";
import { Coordinates } from "types/Positioning";
import { Vector2 } from "types/Vector2";

import { Size, SpanData } from "./NumberLine";
import { NumberLineFraction } from "./NumberLineFraction";
import { numberToFraction } from "./numberToFraction";
import { getAnimatedNumber } from "./utils";

interface Props {
  index: number;
  scale: Vector2;
  widgetSize: Size;
  fullWidth: number;
  maxVisibleSegments: number;
  startNumber: number;
  isSelected: boolean;
  showInFront: boolean;
  stepSetting: number;
  hideNumbers: boolean;
  padding: number;
  data: SpanData;
  onDataChanged: (data: Partial<SpanData>) => void;
  onSelect: () => void;
  onSelectHiddenNumber: (position: string) => void;
  animatedSteps: number[];
  animatedSpanIndex?: number;
}

const blockHeightFactor = 11;

export const NumberLineSpan: FC<Props> = ({
  index,
  scale,
  widgetSize,
  maxVisibleSegments,
  fullWidth,
  startNumber,
  isSelected,
  showInFront,
  stepSetting,
  hideNumbers,
  padding,
  data,
  onDataChanged,
  onSelect,
  onSelectHiddenNumber,
  animatedSteps,
  animatedSpanIndex,
}) => {
  const intervalSize = widgetSize.width / (maxVisibleSegments + 1);

  const size: Size = {
    width: ((data.endNumber - data.startNumber) * intervalSize) / stepSetting,
    height: widgetSize.height / blockHeightFactor / scale.y,
  };

  const position: Coordinates = {
    x: ((data.startNumber - startNumber) / stepSetting + 1) * intervalSize + intervalSize * padding,
    y: data.yPosition * size.height,
  };

  const scaledPosition: Coordinates = { x: position.x, y: position.y * scale.y };
  const scaledSize: Size = { width: size.width, height: size.height * scale.y };
  const scaledPadding = padding * stepSetting;

  const onResize = (e: React.SyntheticEvent, data: ResizeCallbackData) => {
    if (animatedSteps.length) {
      return;
    }

    setSize(
      { width: data.size.width, height: widgetSize.height / blockHeightFactor / scale.y },
      data.handle === "w",
    );
  };

  function setSize(newSize: Size, fromStart: boolean) {
    const difference = Math.round(newSize.width / intervalSize) * stepSetting;
    const endNumber = (fullWidth / intervalSize) * stepSetting - 1 + startNumber;

    if (fromStart) {
      onDataChanged({
        yPosition: data.yPosition,
        startNumber: Math.max(data.endNumber - difference, startNumber),
        endNumber: data.endNumber,
      });
    } else {
      onDataChanged({
        yPosition: data.yPosition,
        startNumber: data.startNumber,
        endNumber: Math.min(data.startNumber + difference, endNumber),
      });
    }
  }

  function setPosition(newPosition: Coordinates) {
    const newScaledPosition: Coordinates = {
      x: newPosition.x * scale.x,
      y: newPosition.y * scale.y,
    };

    const leftNumber =
      startNumber -
      stepSetting +
      Math.round(newScaledPosition.x / intervalSize) * stepSetting -
      scaledPadding;
    const rightNumber = leftNumber + (scaledSize.width / intervalSize) * stepSetting;

    onDataChanged({
      yPosition: Math.round(newPosition.y / size.height),
      startNumber: leftNumber,
      endNumber: rightNumber,
    });
  }

  const widgetClicked = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onSelect();
  };

  const getDisplayNumber = (number: number, animatedStep?: number) => {
    if (animatedSpanIndex !== index || animatedStep === undefined) {
      if (stepSetting === 1) {
        return <>{number}</>;
      } else if (stepSetting === 1 / 10) {
        return <>{number.toFixed(1)}</>;
      } else {
        return <NumberLineFraction {...numberToFraction(number, 1 / stepSetting)} />;
      }
    }

    const [operator, num] = getAnimatedNumber(animatedStep);

    if (stepSetting === 1) {
      return (
        <>
          {number}
          {operator}
          {num}
        </>
      );
    } else if (stepSetting === 1 / 10) {
      return (
        <>
          {number.toFixed(1)}
          {operator}
          {num}
        </>
      );
    } else {
      return (
        <>
          <NumberLineFraction {...numberToFraction(number, 1 / stepSetting)} />
          <span style={{ margin: "0 5px" }}>{operator}</span>
          <NumberLineFraction {...numberToFraction(+num, 1 / stepSetting)} />
        </>
      );
    }
  };

  const selectHiddenNumber = (e: React.MouseEvent<HTMLDivElement>, position: string) => {
    e.stopPropagation();

    if (hideNumbers) {
      onSelectHiddenNumber(position);
    }
  };

  return (
    <Draggable
      disabled={Boolean(animatedSteps.length)}
      position={scaledPosition}
      onDrag={(e, data) => setPosition({ x: data.x / scale.x, y: data.y / scale.y })}
      handle=".handle"
      grid={[intervalSize, widgetSize.height / blockHeightFactor]}
      bounds={{
        left: intervalSize + intervalSize * padding,
        right: fullWidth - scaledSize.width - intervalSize - intervalSize * padding,
        top: 0,
        bottom: scaledSize.height * 6,
      }}
    >
      <CustomResizeBox
        width={size.width}
        height={size.height}
        handle={(h, ref) => <ResizeHandle className={`custom-handle-${h}`} ref={ref} />}
        handleSize={[8, 8]}
        resizeHandles={["w", "e"]}
        onResize={onResize}
        axis="x"
        draggableOpts={{
          grid: [intervalSize / scale.x, widgetSize.height / blockHeightFactor / scale.y],
          scale: scale.x,
        }}
        minConstraints={[intervalSize / scale.x, scaledSize.height]}
      >
        <ResizeInner
          isSelected={isSelected}
          showInFront={showInFront}
          style={{ width: scaledSize.width, height: scaledSize.height * 0.9 }}
          onClick={widgetClicked}
        >
          {data.showEnds && (
            <LeftNumber
              style={{ height: intervalSize }}
              onClick={(e) => selectHiddenNumber(e, "left")}
            >
              {getDisplayNumber(data.startNumber)}
              {hideNumbers && (
                <NumberHider
                  style={{
                    display: data.hiddenNumbers.left ? "inline-block" : "none",
                    right: "0px",
                    transform: "translate(0%, -45%)",
                  }}
                  onClick={(e) => selectHiddenNumber(e, "left")}
                >
                  <NumberLineBoxQuestionMark />
                </NumberHider>
              )}
            </LeftNumber>
          )}
          {data.showLength && (
            <CenterNumber
              style={{ pointerEvents: hideNumbers ? "all" : "none" }}
              onClick={(e) => selectHiddenNumber(e, "center")}
            >
              {getDisplayNumber(data.endNumber - data.startNumber, animatedSteps[0])}

              {hideNumbers && (
                <NumberHider
                  style={{
                    width: "50%",
                    display: data.hiddenNumbers.center ? "inline-block" : "none",
                    transform: "translate(0%, -50%)",
                  }}
                  onClick={(e) => selectHiddenNumber(e, "center")}
                >
                  <NumberLineBoxQuestionMark />
                </NumberHider>
              )}
            </CenterNumber>
          )}
          <div className="handle" style={{ width: "100%", height: "100%", position: "absolute" }} />
          {data.showEnds && (
            <RightNumber
              style={{ height: intervalSize }}
              onClick={(e) => selectHiddenNumber(e, "right")}
            >
              {getDisplayNumber(data.endNumber)}
              {hideNumbers && (
                <NumberHider
                  style={{
                    display: data.hiddenNumbers.right ? "inline-block" : "none",
                    transform: "translate(0%, -45%)",
                    left: "0px",
                  }}
                  onClick={(e) => selectHiddenNumber(e, "right")}
                >
                  <NumberLineBoxQuestionMark />
                </NumberHider>
              )}
            </RightNumber>
          )}
        </ResizeInner>
      </CustomResizeBox>
    </Draggable>
  );
};

const CustomResizeBox = styled(Resizable)`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background: #ccc;
  box-sizing: border-box;
  overflow: visible;
  position: relative;
`;

const ResizeInner = styled.div<{ isSelected: boolean; showInFront: boolean }>`
  --span-color: ${(props) => (props.isSelected ? "var(--widget-primary)" : "#40CFCA")};
  background-color: var(--span-color);
  padding: 5px;
  border-radius: 5px;
  justify-content: center;
  align-items: center;
  z-index: ${(props) => (props.showInFront ? "1" : "0")};
`;

const ResizeHandle = styled.span`
  position: absolute;
  width: 8px;
  height: 8px;
  background-color: #1153aa;
  opacity: 0.75;
  border-radius: 4px;

  &.custom-handle-sw {
    bottom: -4px;
    left: -4px;
    cursor: sw-resize;
  }
  &.custom-handle-se {
    bottom: -4px;
    right: -4px;
    cursor: se-resize;
  }
  &.custom-handle-nw {
    top: -4px;
    left: -4px;
    cursor: nw-resize;
  }
  &.custom-handle-ne {
    top: -4px;
    right: -4px;
    cursor: ne-resize;
  }
  &.custom-handle-w,
  &.custom-handle-e {
    top: 50%;
    margin-top: -4px;
    cursor: ew-resize;
  }
  &.custom-handle-w {
    left: -4px;
  }
  &.custom-handle-e {
    right: -4px;
  }
  &.custom-handle-n,
  &.custom-handle-s {
    left: 50%;
    margin-left: -4px;
    cursor: ns-resize;
  }
  &.custom-handle-n {
    top: -4px;
  }
  &.custom-handle-s {
    bottom: -4px;
  }
`;

const LeftNumber = styled(Row)`
  position: absolute;
  left: -1cqw;
  transform: translate(-110%, 0);
  justify-content: flex-end;
  align-items: center;
  font-size: 2.5cqw;
  font-weight: 600;
  line-height: 1em;
`;

const RightNumber = styled(Row)`
  position: absolute;
  right: -1cqw;
  transform: translate(110%, 0);
  justify-content: flex-start;
  align-items: center;
  font-size: 2.5cqw;
  font-weight: 600;
  line-height: 1em;
`;

const CenterNumber = styled(Row)`
  color: white;
  font-size: 2.5cqw;
  font-weight: 600;
  line-height: 1em;
  z-index: 10;
  justify-content: center;
  align-items: center;
`;

const NumberHider = styled.div`
  position: absolute;
  height: 4cqw;
  min-width: 4cqw;
  width: 100%;
  top: 50%;
  color: var(--span-color);
  background-color: var(--span-color);
  border-radius: 3px;
  cursor: pointer;
  z-index: 1;

  svg {
    width: 100%;
    height: 100%;
  }
`;
